import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { VIDEOS } from "@/i18n/assets";
import { toAbsolute } from "@/lib/api";

export default function IntroVideo() {
  const { t, lang } = useLang();
  const { settings: s } = useSite();
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const tag = s[`intro_tag_${lang}`] || t.intro.tag;
  const title = s[`intro_title_${lang}`] || t.intro.title;
  const subtitle = s[`intro_subtitle_${lang}`] || t.intro.subtitle;
  const videoUrl = toAbsolute(s.intro_video_url) || VIDEOS.intro;
  const posterUrl = toAbsolute(s.intro_poster_url) || VIDEOS.introPoster;

  const play = () => {
    setPlaying(true);
    setTimeout(() => {
      const p = videoRef.current?.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }, 60);
  };

  return (
    <section id="intro" className="relative section-pad" data-testid="intro-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl mb-10">
          <Reveal><SectionTag>{tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 mb-4">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg leading-relaxed">{subtitle}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-video group" data-testid="intro-video-wrap">
            <video
              ref={videoRef}
              key={videoUrl}
              src={videoUrl}
              poster={posterUrl}
              controls={playing}
              loop
              playsInline
              className="h-full w-full object-cover"
              data-testid="intro-video-el"
              onError={() => setPlaying(true)}
            />
            {!playing && (
              <button
                onClick={play}
                className="absolute inset-0 grid place-items-center"
                style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.35), rgba(5,9,20,0.7))" }}
                data-testid="intro-play-btn"
                aria-label={t.intro.play}
              >
                <motion.span
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative h-20 w-20 rounded-full grid place-items-center"
                  style={{ background: "var(--primary)", boxShadow: "0 0 40px rgba(0,242,96,0.5)" }}
                >
                  <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "rgba(0,242,96,0.35)" }} />
                  <Play size={30} className="text-[#04120a] ms-1 relative z-10" fill="#04120a" />
                </motion.span>
                <span className="absolute bottom-6 start-6 text-lg font-bold">{t.intro.play}</span>
              </button>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
